import React from 'react';

import { Pie, PieChart } from 'recharts';

import { useAppSelector } from '@/hooks/redux';
import { useTheme } from '@/hooks/useTheme';
import { UserDetailRes } from '@/redux/wallet/model';
import { USDDollarFormatter } from '@/utils/formatter';
import { cn } from '@/utils/helper';

interface UserStatsListProps {
  nettyWorth: number;
  userDetail: UserDetailRes;
  isSideBarMenu?: boolean;
}

const UserStatsList: React.FC<UserStatsListProps> = ({ nettyWorth, userDetail, isSideBarMenu = 'true' }) => {
  const { mode } = useTheme();
  const loan = useAppSelector((state) => state.loans.loan);

  const data = loan?.borrower
    ? [
        { name: 'Defaulted Loans', value: loan?.borrower.foreclosedCount, fill: `${mode === 'light' ? '#E4E6F1' : '#333333'}`, cornerRadius: 40 },
        { name: 'Repaid Loans', value: loan?.borrower.paidCount, fill: '#1160FF', cornerRadius: 0 },
      ]
    : null;

  const panels = [
    {
      id: 1,
      txt: 'NettyWorth',
      value: USDDollarFormatter(nettyWorth || 0),
      preFix: true,
    },
    {
      id: 2,
      txt: 'Repayment Rate',
      unit: '%',
      value: (
        <>
          {!isSideBarMenu && data && (
            <PieChart width={24} height={24} className='-rotate-90'>
              <Pie data={data} innerRadius={10} outerRadius={12} paddingAngle={0} strokeWidth={0} dataKey='value' />
            </PieChart>
          )}
          {userDetail?.nettyScore ?? 0}
        </>
      ),
      postFix: true,
    },
    ...(isSideBarMenu
      ? [
          {
            id: 3,
            txt: 'Active Loans',
            value: userDetail?.activeCount ?? 0,
          },
          {
            id: 4,
            txt: 'Loan Offers',
            value: userDetail?.loanOffers ?? 0,
          },
          {
            id: 5,
            txt: 'Lending',
            value: userDetail?.lendingCount ?? 0,
          },
        ]
      : [
          {
            id: 3,
            txt: 'Total Loans',
            value: loan?.borrower.paidCount ?? 0 + (loan?.borrower.foreclosedCount ?? 0),
          },
          {
            id: 6,
            txt: 'Repaid Loans',
            value: loan?.borrower.paidCount,
          },
          {
            id: 7,
            txt: 'Defaulted Loans',
            value: loan?.borrower.foreclosedCount,
          },
        ]),
  ];

  return (
    <ul className={cn(mode === 'dark' ? 'border-b-coal-100' : 'border-light-blue-grey', isSideBarMenu ? 'pb-4 ss:pb-6 lg:pb-0 max-lg:border-b flex flex-col gap-4 ss:gap-7 flex-1' : 'pt-[10px] flex flex-col')}>
      {panels.map(({ id, txt, value, preFix, postFix, unit }, idx) => {
        return (
          <li key={id} className={cn(isSideBarMenu ? '' : 'border-b px-6 py-[17px] max-h-[55px]', 'flex justify-between items-center gap-1')}>
            <p className='text-sm ss:text-base/5 font-medium'>{txt}</p>
            <p className={cn(idx === 0 ? '!text-green-250' : '', isSideBarMenu ? 'text-sm ss:text-base/5 font-medium' : 'text-sm/5 font-normal font-space-mono', 'flex items-center gap-3')}>
              {preFix && unit}
              {value}
              {postFix && unit}
            </p>
          </li>
        );
      })}
    </ul>
  );
};

export default UserStatsList;
